import type { Branded, FetchFunction, ResponseEnvelope, ValueOf } from '#layers/base/public';
import type { MonospaceFile, SystemMonospaceFile } from '#layers/data-model/public';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useFileUploadLimit } from '#layers/assets/composables/use-file-upload-limit';
import { isPromiseResultFulfilled, randomUuid, useSystemFetch, useWorkspaceFetchFactory } from '#layers/base/public';
import { useSystemEntityStore, useWorkspaceRuntimeHandle } from '#layers/runtime/public';
import { useWorkspaceStore } from '#layers/workspace/public';

export type UploadId = Branded<string, 'UploadId'>;

export const UploadScope = {
	System: 'system',
	Workspace: 'workspace',
} as const;

export type UploadScope = ValueOf<typeof UploadScope>;

export interface UploadingFile {
	id: UploadId;
	file: File;
	size: number;
	status: 'pending' | 'uploading' | 'success' | 'error' | 'aborted';
	scope: UploadScope;
	workspaceSlug?: string;
	abort?: () => void;
	error?: string;
	uploadedFileId?: string;
}

export interface UploadFileOptions {
	scope?: UploadScope;
	onStart?: (uploadId: UploadId) => void;
	onSuccess?: (file: MonospaceFile | SystemMonospaceFile) => void;
	onError?: (error: unknown) => void;
}

export const useUploadStore = defineStore('upload', () => {
	const { resolveUploadErrorMessage } = useFileUploadLimit();
	const workspaceStore = useWorkspaceStore();
	const systemFetch = useSystemFetch();
	const createWorkspaceFetch = useWorkspaceFetchFactory();

	const files = ref<Map<UploadId, UploadingFile>>(new Map());
	const allFiles = computed(() => [...files.value.values()].filter((file) =>
		file.scope === UploadScope.System || file.workspaceSlug === workspaceStore.currentWorkspaceSlug,
	));

	const uploadingFiles = computed(() => allFiles.value.filter((f) => f.status === 'uploading' || f.status === 'pending'));
	const successfulFiles = computed(() => allFiles.value.filter((f) => f.status === 'success'));
	const failedFiles = computed(() => allFiles.value.filter((f) => f.status === 'error'));
	const abortedFiles = computed(() => allFiles.value.filter((f) => f.status === 'aborted'));
	const completedFiles = computed(() => allFiles.value.filter((f) => isFileCompleted(f)));

	workspaceStore.onLifecycleTransition(() => removeHiddenCompletedFiles());

	async function uploadSystemFile(file: File, signal: AbortSignal): Promise<SystemMonospaceFile> {
		const systemStore = useSystemEntityStore();

		return systemStore.SystemMonospaceFile.mutate({ mutation: 'create' }, async () => {
			const uploadedFileId = await uploadAsset({
				endpoint: '/system/assets',
				fetch: systemFetch,
				file,
				signal,
			});
			const uploadedFile = await systemStore.SystemMonospaceFile.findFirst({ key: uploadedFileId });

			if (!uploadedFile) throw new Error('File not found');
			return uploadedFile;
		});
	}

	async function uploadFile(file: File, options?: UploadFileOptions): Promise<MonospaceFile | SystemMonospaceFile | undefined> {
		const scope = options?.scope ?? UploadScope.Workspace;
		const uploadId = addFile(file, scope);

		const abortController = new AbortController();

		// check if file was aborted in the meantime (highly unlikely, but does not hurt)
		if (getFile(uploadId)?.status === 'aborted') return;

		markUploading(uploadId, () => abortController.abort());
		options?.onStart?.(uploadId);

		try {
			let fullFile: MonospaceFile | SystemMonospaceFile;

			if (scope === UploadScope.System) {
				fullFile = await uploadSystemFile(file, abortController.signal);
			}
			else {
				const handle = useWorkspaceRuntimeHandle();
				const upload = getFile(uploadId);
				if (upload) upload.workspaceSlug = handle.slug;

				// The transport is bound to the slug without the runtime signal so the upload survives a workspace switch.
				const uploadedFileId = await uploadAsset({
					endpoint: '/assets',
					fetch: createWorkspaceFetch({ workspaceSlug: handle.slug }),
					file,
					signal: abortController.signal,
				});

				// A retired runtime's store is never written, and nothing alive can consume the entity.
				if (!handle.isActive()) {
					markSuccess(uploadId, uploadedFileId);
					return;
				}

				try {
					// Tracked so a switch during the follow-up read drains it instead of aborting a finished upload.
					fullFile = await handle.run(() => handle.store.MonospaceFile.mutate({ mutation: 'create' }, async () => {
						const uploadedFile = await handle.store.MonospaceFile.findFirst({ key: uploadedFileId });

						if (!uploadedFile) throw new Error('File not found');
						return uploadedFile;
					}));
				}
				catch (error) {
					// The asset exists server-side; only the retired runtime's read was cut.
					if (handle.signal.aborted) {
						markSuccess(uploadId, uploadedFileId);
						return;
					}

					throw error;
				}
			}

			markSuccess(uploadId, fullFile.id);

			options?.onSuccess?.(fullFile);

			return fullFile;
		}
		catch (error) {
			if (abortController.signal.aborted) {
				markAborted(uploadId);
				return;
			}

			markError(uploadId, resolveUploadErrorMessage(error));
			options?.onError?.(error);
		}
	}

	async function uploadFiles(files: File[], options?: UploadFileOptions): Promise<MonospaceFile[]> {
		const results = await Promise.allSettled(files.map((file) => uploadFile(file, options)));

		return results
			.filter((result): result is PromiseFulfilledResult<MonospaceFile> => isPromiseResultFulfilled(result) && !!result.value)
			.map((result) => result.value);
	}

	function addFile(file: File, scope: UploadScope): UploadId {
		const uploadId = `${randomUuid()}-${file.name}` as UploadId;

		files.value.set(uploadId, {
			id: uploadId,
			file,
			status: 'pending',
			size: file.size,
			scope,
		});

		return uploadId;
	}

	function getFile(uploadId: UploadId) {
		return files.value.get(uploadId);
	}

	function markUploading(uploadId: UploadId, abort: () => void) {
		const file = files.value.get(uploadId);

		if (file) {
			file.status = 'uploading';
			file.abort = abort;
		}
	}

	function markSuccess(uploadId: UploadId, uploadedFileId: string) {
		const file = files.value.get(uploadId);

		if (file) {
			file.status = 'success';
			file.uploadedFileId = uploadedFileId;
			removeIfHidden(file);
		}
	}

	function markError(uploadId: UploadId, error: string) {
		const file = files.value.get(uploadId);

		if (file && file.status !== 'aborted') {
			file.status = 'error';
			file.error = error;
			removeIfHidden(file);
		}
	}

	function markAborted(uploadId: UploadId) {
		const file = files.value.get(uploadId);

		if (file) {
			file.status = 'aborted';
			removeIfHidden(file);
		}
	}

	function removeHiddenCompletedFiles() {
		for (const file of files.value.values()) {
			if (isFileCompleted(file)) removeIfHidden(file);
		}
	}

	function removeIfHidden(file: UploadingFile) {
		if (file.scope === UploadScope.Workspace && file.workspaceSlug !== workspaceStore.currentWorkspaceSlug) {
			files.value.delete(file.id);
		}
	}

	function removeFile(uploadId: UploadId) {
		files.value.delete(uploadId);
	}

	function abortUpload(uploadId: UploadId) {
		const file = files.value.get(uploadId);

		if (file && (file.status === 'uploading' || file.status === 'pending')) {
			file.abort?.();
			markAborted(uploadId);
		}
	}

	function clearCompleted() {
		for (const file of completedFiles.value) {
			files.value.delete(file.id);
		}
	}

	function isFileCompleted(file: UploadingFile) {
		return file.status === 'success' || file.status === 'error' || file.status === 'aborted';
	}

	return {
		allFiles,
		uploadingFiles,
		successfulFiles,
		abortedFiles,
		failedFiles,
		completedFiles,
		uploadFile,
		uploadFiles,
		abortUpload,
		removeFile,
		clearCompleted,
		getFile,
	};
});

/** Performs only the multipart transport shared by system and workspace uploads. */
async function uploadAsset({ endpoint, fetch, file, signal }: { endpoint: string; fetch: FetchFunction; file: File; signal: AbortSignal }): Promise<string> {
	const formData = new FormData();
	formData.append('file', file);

	const { data } = await fetch<ResponseEnvelope<Pick<MonospaceFile, 'id'>>>(endpoint, {
		method: 'POST',
		body: formData,
		signal,
	});

	const uploadedFile = Array.isArray(data) ? data[0] : data;
	return uploadedFile.id;
}
